import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Activity } from 'lucide-react';
import { api } from '@/lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/ui/empty-state';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatDate } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth';
import { ROLE_RANK } from '@praxara/shared/constants';

interface SkillEval {
  id: string;
  skillName: string;
  status: string | null;
  totalCases: number | null;
  passedCases: number | null;
  failedCases: number | null;
  createdAt: string;
}

interface SkillGroup {
  skillName: string;
  total: number;
  passed: number;
  failed: number;
  lastRun: string | null;
  latestId: string | null;
}

export function AdminDriftPage() {
  const user = useAuthStore((s) => s.user);
  const isAdmin = user && (user.isSuperAdmin || ROLE_RANK[user.role] >= ROLE_RANK['ADMIN']);

  const evals = useQuery({
    queryKey: ['admin-skill-evals'],
    queryFn: async () => {
      try {
        return (await api.get('/api/admin/skill-evals?pageSize=200')).data;
      } catch {
        return { items: [] };
      }
    },
    enabled: !!isAdmin,
  });

  const items: SkillEval[] = evals.data?.items ?? [];

  const groups: SkillGroup[] = useMemo(() => {
    const map = new Map<string, SkillGroup>();
    for (const e of items) {
      const g =
        map.get(e.skillName) ??
        ({
          skillName: e.skillName,
          total: 0,
          passed: 0,
          failed: 0,
          lastRun: null,
          latestId: null,
        } satisfies SkillGroup);
      g.total += e.totalCases ?? 0;
      g.passed += e.passedCases ?? 0;
      g.failed += e.failedCases ?? 0;
      if (!g.lastRun || new Date(e.createdAt) > new Date(g.lastRun)) {
        g.lastRun = e.createdAt;
        g.latestId = e.id;
      }
      map.set(e.skillName, g);
    }
    return Array.from(map.values()).sort((a, b) => a.skillName.localeCompare(b.skillName));
  }, [items]);

  if (!isAdmin) {
    return (
      <EmptyState
        icon={Activity}
        title="Administrator access required"
        description="Only ADMIN or SUPER_ADMIN users can view drift metrics."
      />
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Model drift - skill evals</h1>
        <p className="text-sm text-muted-foreground">
          Aggregated pass/fail rates from SkillEval runs. Links through to SkillEvalResult details.
        </p>
      </div>

      {evals.isLoading ? (
        <Skeleton className="h-64" />
      ) : groups.length === 0 ? (
        <EmptyState
          icon={Activity}
          title="No skill evals yet"
          description="Run a skill-eval job to populate this dashboard."
        />
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>By skill</CardTitle>
            <CardDescription>
              Totals aggregate across all runs in the current window.
            </CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Skill</TableHead>
                  <TableHead>Total cases</TableHead>
                  <TableHead>Passed</TableHead>
                  <TableHead>Failed</TableHead>
                  <TableHead>Pass rate</TableHead>
                  <TableHead>Last run</TableHead>
                  <TableHead className="text-right">Latest</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {groups.map((g) => {
                  const rate = g.total > 0 ? (g.passed / g.total) * 100 : null;
                  return (
                    <TableRow key={g.skillName}>
                      <TableCell className="font-medium">{g.skillName}</TableCell>
                      <TableCell>{g.total}</TableCell>
                      <TableCell className="text-emerald-600">{g.passed}</TableCell>
                      <TableCell className="text-destructive">{g.failed}</TableCell>
                      <TableCell>
                        {rate != null ? (
                          <Badge
                            variant={rate >= 90 ? 'success' : rate >= 70 ? 'warning' : 'destructive'}
                          >
                            {rate.toFixed(0)}%
                          </Badge>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {g.lastRun ? formatDate(g.lastRun) : '-'}
                      </TableCell>
                      <TableCell className="text-right">
                        {g.latestId ? (
                          <Link
                            to={`/admin/drift/${g.latestId}`}
                            className="text-xs font-medium text-primary hover:underline"
                          >
                            View results
                          </Link>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
